// Synty POLYGON Samurai characters for the hex field: villagers (citizens) and warriors / ninjas (goons).
// Each is a skinned clone with its own skeleton and an authored Idle + Walk cycle. The field moves
// citizens with gsap position tweens, so walking vs standing is detected from actual movement each frame.
//
//   loadSyntyChars()            → idempotent, non-fatal loader (call once at boot)
//   createSyntyChar(kind, opts) → a Group standing at y = 0 (caller positions it), or null if not loaded
//   tickSyntyChars(dt)          → advance animation mixers (call every frame)
//   scaleOf(obj)                → the object's rest scale (Synty chars and the old cats differ)

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { CapitalConfig as CFG } from '../config/capital.js'
import { Field } from '../config/balance.js'
import { HEX_SIZE } from './hexGrid.js'

const FILES = { villager_f: 'villager_f.glb', villager_m: 'villager_m.glb', warrior: 'warrior.glb', ninja: 'ninja.glb', geisha: 'geisha.glb' }
const WALK_CYCLE_SPEED = 1.5            // m/s the authored walk is built for
const TORCH_WALK_SPEED = 1.43           // m/s the Mixamo torch walk covers (1.62 m in 34 frames at 30 fps)
const SCALE = HEX_SIZE / 6.83           // source metres → world units (same as the Synty buildings)

let ready = null
const gltfs = {}
let textures = []
const live = new Set()

export function loadSyntyChars() {
  if (ready) return ready
  const dir = `${CFG.dir}/chars`
  const loader = new GLTFLoader()
  const tex = (n) => new Promise((resolve, reject) => new THREE.TextureLoader().load(`${dir}/Characters_Texture_0${n}.png`, (t) => {
    t.colorSpace = THREE.SRGBColorSpace; t.flipY = false; t.magFilter = t.minFilter = THREE.NearestFilter; t.generateMipmaps = false
    resolve(t)
  }, undefined, reject))
  ready = Promise.all([
    ...Object.entries(FILES).map(([k, f]) => loader.loadAsync(`${dir}/${f}`).then((g) => { gltfs[k] = g })),
    Promise.all([1, 2, 3].map(tex)).then((t) => { textures = t }),
  ]).then(() => true)
  return ready
}
export const syntyCharsReady = () => !!gltfs.warrior && !!gltfs.ninja && !!gltfs.villager_f && !!gltfs.villager_m && textures.length === 3
export const scaleOf = (obj) => obj?.userData?.baseScale ?? Field.catScale

const clipOf = (g, name) => g.animations.find((c) => c.name.toLowerCase() === name)

// opts.palette: 1|2|3 (armour colourway), opts.tint: THREE.Color multiplied into the material (e.g. raiders)
export function createSyntyChar(kind, opts = {}) {
  const g = gltfs[kind]
  if (!g || !textures.length) return null
  const holder = new THREE.Group()
  const model = cloneSkinned(g.scene)
  model.userData.role = 'body' // hunger / injury scaling finds the body by role
  const mat = new THREE.MeshLambertMaterial({ map: textures[(opts.palette ?? 1) - 1], side: THREE.DoubleSide })
  mat.color.setScalar(CFG.brightness)
  if (opts.tint) mat.color.multiply(opts.tint)
  if (opts.emissive) mat.emissive = new THREE.Color(opts.emissive)
  model.traverse((o) => { if (o.isMesh || o.isSkinnedMesh) { o.material = mat; o.frustumCulled = false } })
  holder.add(model)

  const mixer = new THREE.AnimationMixer(model)
  const walk = clipOf(g, 'walk') && mixer.clipAction(clipOf(g, 'walk'))
  const idle = clipOf(g, 'idle') && mixer.clipAction(clipOf(g, 'idle'))
  const t0 = Math.random()
  for (const a of [walk, idle]) if (a) { a.play(); a.time = t0 * a.getClip().duration; a.setEffectiveWeight(0) }
  const actions = {}
  for (const clip of g.animations) actions[clip.name.toLowerCase()] = mixer.clipAction(clip)
  const s = { holder, mixer, walk, idle, actions, mode: 'auto', cur: null, last: new THREE.Vector3(), first: true, moving: false }
  const play = (next) => {
    if (!next || s.cur === next) return
    next.setEffectiveWeight(1)
    if (s.cur) { next.reset().fadeIn(0.25).play(); s.cur.fadeOut(0.25) } else next.play()
    s.cur = next
  }
  s.play = play
  play(idle ?? walk)

  holder.userData.synty = s
  holder.userData.mat = mat
  holder.userData.baseScale = SCALE
  // Where overlays sit on a ~1.8 m character (local metres, before the holder's scale).
  holder.userData.ui = { capY: 1.8, capScale: 0.5, markY: 2.1, markScale: 1.1, haloY: 2.0, haloScale: 0.6, torch: { x: 0.45, y: 0.95, z: 0.2, k: 1.6 } }
  holder.scale.setScalar(SCALE)
  live.add(holder)
  return holder
}

const _p = new THREE.Vector3()
export function tickSyntyChars(dt) {
  if (!dt) return
  for (const h of live) {
    if (!h.parent) { live.delete(h); continue }
    const s = h.userData.synty
    s.mixer.update(dt)
    h.getWorldPosition(_p)
    if (s.first) { s.last.copy(_p); s.first = false; continue }
    const speed = Math.hypot(_p.x - s.last.x, _p.z - s.last.z) / dt // world units / s
    s.last.copy(_p)
    s.speed = speed
    if (s.mode === 'manual') { // a game-driven clip is in charge; only match walk-cycle speed to actual movement
      const tw = s.actions.torchwalk
      if (s.cur === tw) tw.setEffectiveTimeScale(Math.max(0.6, Math.min(2.4, speed / (TORCH_WALK_SPEED * SCALE))))
      continue
    }
    const moving = speed > 0.04
    if (moving !== s.moving) { s.moving = moving; s.play(moving ? (s.walk ?? s.idle) : (s.idle ?? s.walk)) }
    if (moving && s.walk) s.walk.setEffectiveTimeScale(Math.max(0.5, Math.min(2.6, speed / (WALK_CYCLE_SPEED * SCALE))))
  }
}

// Play a named clip (lower-case: 'torchwalk', 'torchlight', 'torchburn', 'death'…) on a character, taking it out of
// automatic walk/idle control. once = play one time and hold the last frame (death). Returns the action or null.
export function playClip(holder, name, { fade = 0.25, once = false, timeScale = 1, onDone } = {}) {
  const s = holder?.userData?.synty
  const a = s?.actions?.[name.toLowerCase()]
  if (!a) return null
  s.mode = 'manual'
  if (s.cur === a && !once) return a
  a.reset()
  a.enabled = true
  a.setEffectiveWeight(1)
  a.setEffectiveTimeScale(timeScale)
  a.setLoop(once ? THREE.LoopOnce : THREE.LoopRepeat, Infinity)
  a.clampWhenFinished = once
  if (s.cur && s.cur !== a) { a.fadeIn(fade).play(); s.cur.fadeOut(fade) } else a.play()
  s.cur = a
  if (onDone) {
    const h = (e) => { if (e.action === a) { s.mixer.removeEventListener('finished', h); onDone() } }
    s.mixer.addEventListener('finished', h)
  }
  return a
}
export const clipDuration = (holder, name) => holder?.userData?.synty?.actions?.[name.toLowerCase()]?.getClip().duration ?? 0
